import { Command as CommandPrimitive } from "cmdk"
import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react"

import { api, type ContratoNaLista } from "../api"
import { Command, CommandGroup, CommandItem, CommandList } from "../componentes/Command"
import * as Icones from "../componentes/Icones"
import { numeroComAno } from "../formato"
import { cn } from "../lib/cn"
import { TAMANHO_MINIMO, filtrarDestinos, type DestinoDaBusca } from "./destinos-da-busca"

const ESPERA_ANTES_DE_BUSCAR = 250
const CONTRATOS_POR_BUSCA = 5
const RECENTES_GUARDADOS = 5
const TELAS_MAIS_USADAS = 5
const NOME = "Buscar telas e contratos"

type Recente =
  | { tipo: "tela"; chave: string }
  | { tipo: "contrato"; id: number; titulo: string; detalhe: string }

type EstadoDaBusca = "parada" | "buscando" | "pronta" | "falhou"

type Props = {
  destinos: DestinoDaBusca[]
  atual: string | null
  usuarioId: number
  aoAbrirContrato: (id: number) => void
  aoVerTodos: (termo: string) => void
}

function ler<T>(chave: string, padrao: T): T {
  try {
    const guardado = window.localStorage.getItem(chave)
    return guardado === null ? padrao : (JSON.parse(guardado) as T)
  } catch {
    return padrao
  }
}

function gravar(chave: string, valor: unknown): void {
  try {
    window.localStorage.setItem(chave, JSON.stringify(valor))
  } catch {
    return
  }
}

function tituloDoContrato(contrato: ContratoNaLista): string {
  return `Contrato ${numeroComAno(contrato.numero, contrato.ano)}`
}

function detalheDoContrato(contrato: ContratoNaLista): string {
  return [contrato.contratado?.razao_social, contrato.objeto].filter(Boolean).join(" · ")
}

const CAMPO_DA_BUSCA =
  "h-8 w-full rounded-(--raio-controle) border border-(--borda-forte) bg-(--painel) pr-14 pl-8 text-(length:--texto-sm) text-(--tinta) outline-none placeholder:text-(--tinta-3) focus-visible:border-(--foco) focus-visible:ring-2 focus-visible:ring-(--foco)/30 in-data-[slot=faixa]:border-[rgb(255_255_255/0.28)] in-data-[slot=faixa]:bg-[rgb(255_255_255/0.12)] in-data-[slot=faixa]:text-(--rail-tinta-forte) in-data-[slot=faixa]:placeholder:text-(--azul-100) in-data-[slot=faixa]:hover:bg-[rgb(255_255_255/0.2)] in-data-[slot=faixa]:focus:border-transparent in-data-[slot=faixa]:focus:bg-(--painel) in-data-[slot=faixa]:focus:text-(--tinta) in-data-[slot=faixa]:focus:placeholder:text-(--tinta-3)" // anti-ia-ok: o mesmo branco translúcido do BOTAO_DA_CASCA

const LUPA =
  "pointer-events-none absolute top-1/2 left-2.5 flex -translate-y-1/2 text-(--tinta-3) in-data-[slot=faixa]:text-(--azul-100) group-focus-within/busca:text-(--tinta-3)"

const TECLA = "rounded-(--raio-1) border border-(--borda-forte) bg-(--painel) px-1 text-(length:--texto-xs) text-(--tinta)"

const QUADRO_DO_ICONE = "flex size-8 flex-none items-center justify-center rounded-(--raio-2) bg-(--painel-alt) text-(--acao)"

function Opcao({
  valor,
  titulo,
  detalhe,
  icone: Icone,
  aoEscolher,
}: {
  valor: string
  titulo: string
  detalhe?: string
  icone: Icones.Icone
  aoEscolher: () => void
}) {
  return (
    <CommandItem value={valor} onSelect={aoEscolher} className="cursor-pointer gap-3 py-2">
      <span className={QUADRO_DO_ICONE} aria-hidden="true">
        <Icone tamanho={16} />
      </span>
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span className="truncate font-medium text-(--tinta)">{titulo}</span>
        {detalhe !== undefined && detalhe !== "" && (
          <span className="truncate text-(length:--texto-xs) text-(--tinta-3)">{detalhe}</span>
        )}
      </span>
    </CommandItem>
  )
}

export function BuscaGeral({ destinos, atual, usuarioId, aoAbrirContrato, aoVerTodos }: Props) {
  const chaveDosRecentes = `contratos.busca.recentes.${usuarioId}`
  const chaveDasVisitas = `contratos.busca.visitas.${usuarioId}`
  const [recentes, definirRecentes] = useState<Recente[]>(() => ler<Recente[]>(chaveDosRecentes, []))
  const [visitas, definirVisitas] = useState<Record<string, number>>(() => ler<Record<string, number>>(chaveDasVisitas, {}))
  const [termo, definirTermo] = useState("")
  const [aberta, definirAberta] = useState(false)
  const [abertaNoCelular, definirAbertaNoCelular] = useState(false)
  const [contratos, definirContratos] = useState<ContratoNaLista[]>([])
  const [estado, definirEstado] = useState<EstadoDaBusca>("parada")
  const [ativa, definirAtiva] = useState("")
  const caixa = useRef<HTMLDivElement>(null)
  const campo = useRef<HTMLInputElement>(null)

  useEffect(() => {
    definirRecentes(ler<Recente[]>(chaveDosRecentes, []))
    definirVisitas(ler<Record<string, number>>(chaveDasVisitas, {}))
  }, [chaveDosRecentes, chaveDasVisitas])

  useEffect(() => {
    if (atual === null) return
    const atuais = ler<Record<string, number>>(chaveDasVisitas, {})
    const novas = { ...atuais, [atual]: (atuais[atual] ?? 0) + 1 }
    gravar(chaveDasVisitas, novas)
    definirVisitas(novas)
  }, [atual, chaveDasVisitas])

  const abrirCampo = () => {
    definirAbertaNoCelular(true)
    definirAberta(true)
    requestAnimationFrame(() => campo.current?.focus())
  }

  const fechar = () => {
    definirAberta(false)
    definirAbertaNoCelular(false)
  }

  useEffect(() => {
    const aoTeclar = (evento: globalThis.KeyboardEvent) => {
      if ((evento.ctrlKey || evento.metaKey) && evento.key.toLowerCase() === "k") {
        evento.preventDefault()
        abrirCampo()
      }
    }
    const aoSairDaCaixa = (evento: Event) => {
      if (caixa.current !== null && !caixa.current.contains(evento.target as Node)) fechar()
    }
    window.addEventListener("keydown", aoTeclar)
    document.addEventListener("mousedown", aoSairDaCaixa)
    document.addEventListener("focusin", aoSairDaCaixa)
    return () => {
      window.removeEventListener("keydown", aoTeclar)
      document.removeEventListener("mousedown", aoSairDaCaixa)
      document.removeEventListener("focusin", aoSairDaCaixa)
    }
  }, [])

  const procurado = termo.trim()
  const vazio = procurado === ""
  const buscavel = procurado.length >= TAMANHO_MINIMO

  useEffect(() => {
    if (!buscavel) {
      definirContratos([])
      definirEstado("parada")
      return
    }
    let vivo = true
    definirEstado("buscando")
    const espera = window.setTimeout(() => {
      const consulta = new URLSearchParams({ busca: procurado, pagina: "1", por_pagina: String(CONTRATOS_POR_BUSCA) })
      api
        .contratos(`?${consulta.toString()}`)
        .then((resposta) => {
          if (!vivo) return
          definirContratos(resposta.dados)
          definirEstado("pronta")
        })
        .catch(() => {
          if (!vivo) return
          definirContratos([])
          definirEstado("falhou")
        })
    }, ESPERA_ANTES_DE_BUSCAR)
    return () => {
      vivo = false
      window.clearTimeout(espera)
    }
  }, [procurado, buscavel])

  const telas = useMemo(() => filtrarDestinos(destinos, procurado), [destinos, procurado])
  const porChave = useMemo(() => new Map(destinos.map((d) => [d.chave, d])), [destinos])

  const recentesVisiveis = useMemo(
    () => recentes.filter((r) => r.tipo === "contrato" || porChave.has(r.chave)),
    [recentes, porChave],
  )
  const maisUsadas = useMemo(
    () =>
      Object.entries(visitas)
        .sort(([, a], [, b]) => b - a)
        .map(([chave]) => porChave.get(chave))
        .filter((d): d is DestinoDaBusca => d !== undefined)
        .slice(0, TELAS_MAIS_USADAS),
    [visitas, porChave],
  )

  const lembrar = (recente: Recente) => {
    const igual = (r: Recente) =>
      r.tipo === recente.tipo && (r.tipo === "tela" ? r.chave === (recente as { chave: string }).chave : r.id === (recente as { id: number }).id)
    const novos = [recente, ...recentes.filter((r) => !igual(r))].slice(0, RECENTES_GUARDADOS)
    gravar(chaveDosRecentes, novos)
    definirRecentes(novos)
  }

  const concluir = () => {
    definirTermo("")
    fechar()
  }

  const abrirDestino = (destino: DestinoDaBusca) => {
    lembrar({ tipo: "tela", chave: destino.chave })
    concluir()
    destino.abrir()
  }

  const abrirContrato = (id: number, titulo: string, detalhe: string) => {
    lembrar({ tipo: "contrato", id, titulo, detalhe })
    concluir()
    aoAbrirContrato(id)
  }

  const verTodos = () => {
    const alvo = procurado
    concluir()
    aoVerTodos(alvo)
  }

  const aoTeclarNoCampo = (evento: KeyboardEvent<HTMLInputElement>) => {
    if (evento.key === "Escape") {
      evento.preventDefault()
      fechar()
    }
  }

  const temSugestoes = recentesVisiveis.length > 0 || maisUsadas.length > 0
  const mostrarPainel = aberta && (buscavel || (vazio && temSugestoes))
  const nada = buscavel && estado === "pronta" && telas.length === 0 && contratos.length === 0

  return (
    <div
      ref={caixa}
      data-slot="busca-geral"
      data-aberta-no-celular={abertaNoCelular ? "true" : "false"}
      className="relative flex flex-none items-center"
    >
      <button
        type="button"
        className="hidden size-8 cursor-pointer items-center justify-center rounded-(--raio-2) border-0 bg-transparent text-(--tinta) in-data-[slot=faixa]:text-(--rail-tinta-forte) max-[767px]:inline-flex"
        aria-label={NOME}
        title={NOME}
        onClick={abrirCampo}
      >
        <Icones.Buscar tamanho={18} />
      </button>

      <Command
        label={NOME}
        shouldFilter={false}
        loop
        value={ativa}
        onValueChange={definirAtiva}
        className={cn(
          "group/busca relative h-auto w-[min(14rem,22vw)] min-w-44 overflow-visible bg-transparent",
          abertaNoCelular
            ? "max-[767px]:fixed max-[767px]:inset-x-3 max-[767px]:top-3 max-[767px]:z-50 max-[767px]:w-auto max-[767px]:min-w-0"
            : "max-[767px]:hidden",
        )}
      >
        <span className={LUPA} aria-hidden="true">
          <Icones.Buscar tamanho={15} />
        </span>
        <CommandPrimitive.Input
          ref={campo}
          value={termo}
          onValueChange={(valor) => {
            definirTermo(valor)
            definirAberta(true)
          }}
          onFocus={() => definirAberta(true)}
          onClick={() => definirAberta(true)}
          onKeyDown={aoTeclarNoCampo}
          placeholder="Buscar"
          className={CAMPO_DA_BUSCA}
        />
        {termo === "" && (
          <kbd
            data-slot="atalho-da-busca"
            className="pointer-events-none absolute top-1/2 right-2 -translate-y-1/2 rounded-(--raio-1) border border-(--borda-forte) px-1 text-(length:--texto-xs) text-(--tinta-3) in-data-[slot=faixa]:border-[rgb(255_255_255/0.28)] in-data-[slot=faixa]:text-(--azul-100) group-focus-within/busca:border-(--borda-forte) group-focus-within/busca:text-(--tinta-3)" // anti-ia-ok: o mesmo branco translúcido do BOTAO_DA_CASCA
          >
            Ctrl K
          </kbd>
        )}

        {mostrarPainel && (
          <div className="absolute top-full right-0 z-50 mt-2 flex max-h-[70vh] w-[min(30rem,calc(100vw-1.5rem))] flex-col overflow-hidden rounded-(--raio-2) border border-(--borda) bg-(--painel) text-(--tinta) shadow-(--elev-3)">
            <CommandList label="Resultados da busca" className="max-h-none min-h-0 flex-1 py-1">
              {vazio && recentesVisiveis.length > 0 && (
                <CommandGroup heading="Abertos recentemente">
                  {recentesVisiveis.map((recente) => {
                    if (recente.tipo === "contrato") {
                      return (
                        <Opcao
                          key={`recente:contrato:${recente.id}`}
                          valor={`recente:contrato:${recente.id}`}
                          titulo={recente.titulo}
                          detalhe={recente.detalhe}
                          icone={Icones.Contrato}
                          aoEscolher={() => abrirContrato(recente.id, recente.titulo, recente.detalhe)}
                        />
                      )
                    }
                    const destino = porChave.get(recente.chave) as DestinoDaBusca
                    return (
                      <Opcao
                        key={`recente:tela:${destino.chave}`}
                        valor={`recente:tela:${destino.chave}`}
                        titulo={destino.titulo}
                        detalhe={destino.detalhe}
                        icone={destino.icone}
                        aoEscolher={() => abrirDestino(destino)}
                      />
                    )
                  })}
                </CommandGroup>
              )}
              {vazio && maisUsadas.length > 0 && (
                <CommandGroup heading="Telas mais usadas">
                  {maisUsadas.map((destino) => (
                    <Opcao
                      key={`usada:${destino.chave}`}
                      valor={`usada:${destino.chave}`}
                      titulo={destino.titulo}
                      detalhe={destino.detalhe}
                      icone={destino.icone}
                      aoEscolher={() => abrirDestino(destino)}
                    />
                  ))}
                </CommandGroup>
              )}
              {!vazio && telas.length > 0 && (
                <CommandGroup heading="Telas">
                  {telas.map((destino) => (
                    <Opcao
                      key={`tela:${destino.chave}`}
                      valor={`tela:${destino.chave}`}
                      titulo={destino.titulo}
                      detalhe={destino.detalhe}
                      icone={destino.icone}
                      aoEscolher={() => abrirDestino(destino)}
                    />
                  ))}
                </CommandGroup>
              )}
              {!vazio && contratos.length > 0 && (
                <CommandGroup heading="Contratos">
                  {contratos.map((contrato) => {
                    const titulo = tituloDoContrato(contrato)
                    const detalhe = detalheDoContrato(contrato)
                    return (
                      <Opcao
                        key={`contrato:${contrato.id}`}
                        valor={`contrato:${contrato.id}`}
                        titulo={titulo}
                        detalhe={detalhe}
                        icone={Icones.Contrato}
                        aoEscolher={() => abrirContrato(contrato.id, titulo, detalhe)}
                      />
                    )
                  })}
                  <CommandItem value="ver-todos" onSelect={verTodos} className="cursor-pointer gap-3 py-2 font-medium text-(--acao)">
                    <span className="flex size-8 flex-none items-center justify-center" aria-hidden="true">
                      <Icones.Avancar tamanho={16} />
                    </span>
                    {`Ver todos os contratos com “${procurado}”`}
                  </CommandItem>
                </CommandGroup>
              )}
              {buscavel && estado === "buscando" && (
                <p role="status" className="m-0 px-4 py-2 text-(length:--texto-xs) text-(--tinta-3)">
                  Buscando contratos…
                </p>
              )}
              {buscavel && estado === "falhou" && (
                <p role="status" className="m-0 px-4 py-2 text-(length:--texto-xs) text-(--tinta-3)">
                  Não foi possível buscar os contratos.
                </p>
              )}
              {nada && (
                <p role="status" className="m-0 px-4 py-3 text-(length:--texto-sm) text-(--tinta-3)">
                  {`Nenhum resultado para “${procurado}”.`}
                </p>
              )}
            </CommandList>
            <div
              data-slot="rodape-da-busca"
              className="flex flex-none flex-wrap items-center gap-x-4 gap-y-1 border-t border-(--borda) bg-(--painel-alt) px-4 py-2 text-(length:--texto-xs) text-(--tinta-3) max-[767px]:hidden"
            >
              <span className="flex items-center gap-1">
                <kbd className={TECLA}>↑</kbd>
                <kbd className={TECLA}>↓</kbd> Navegar
              </span>
              <span className="flex items-center gap-1">
                <kbd className={TECLA}>Enter</kbd> Abrir
              </span>
              <span className="flex items-center gap-1">
                <kbd className={TECLA}>Esc</kbd> Fechar
              </span>
              {vazio && <span className="ml-auto">Digite para buscar</span>}
            </div>
          </div>
        )}
      </Command>
    </div>
  )
}
